import { Big } from 'big.js';
import { match } from 'ts-pattern';
import { DisplayData, TenantJobLine } from '~/lib/model';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import BreJob from '~/tenants/bre/model/BreJob';
import {
  BrePostcardsHandlePay,
  BrePostcardsHandleRate,
  BrePostcardsMailingPay,
  BrePostcardsMailingRate,
  BrePostcardsPostagePay,
  BrePostcardsPostageRate,
  isUpTo,
} from '~/tenants/bre/performable/print/postcards/mailing/BrePostcardsMailingData';
import BrePostcardsMailingConfig, {
  PostcardMailingPaper,
  PostcardMailingPostage,
  PostcardMailingSize,
  PostcardAnnouncementType,
} from '~/tenants/bre/performable/print/postcards/mailing/BrePostcardsMailingConfig';
import { BreDesignPay, BreDesignRate } from '~/tenants/bre/performable/print/design/BreDesignData';
import { BreOrderType } from '~/tenants/bre/model/enums';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';
import BrePhotosConfig from '~/tenants/bre/performable/photos/BrePhotosConfig';
import { datetime } from '~/lib/datettime';

export default class BrePostcardsMailingJob extends BreJob<typeof BrePostcardsMailingConfig> {
  get performable(): typeof BrePostcardsMailingConfig {
    return BrePostcardsMailingConfig;
  }

  get assignee(): string | null {
    return BreProviderId.PRINT_TEAM.toString();
  }

  info(): DisplayData[] {
    const info = super.info();

    info.push({
      name: 'Count',
      value: this.metadata.count.toString(),
      provider: true,
    });

    info.push({
      name: 'Paper',
      value: this.paperType,
      provider: true,
    });

    if (this.announcementType) {
      info.push({
        name: 'Announcement Type',
        value: this.announcementType,
        provider: true,
      });
    }

    info.push({
      name: 'Need By',
      value: datetime(this.metadata.need_by, this.order.location.timezone).toFormat('LLL dd, yyyy'),
      provider: true,
    });

    return info;
  }

  get configurable(): boolean {
    return true;
  }

  get dependencies(): string[] {
    return [BrePhotosConfig.id];
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL, BreOrderType.PROPERTY_MKTG);
  }

  get sizeType(): string {
    const { size } = this.metadata;
    return BrePostcardsMailingJob.sizeType(size);
  }

  get paperType(): string {
    const { paper } = this.metadata;
    return BrePostcardsMailingJob.paperType(paper);
  }

  get announcementType() {
    const { announcement_type } = this.metadata;

    if (!announcement_type) {
      return;
    }
    return BrePostcardsMailingJob.announcementType(announcement_type);
  }

  get postageType(): string {
    const { postage } = this.metadata;
    return BrePostcardsMailingJob.postageType(postage);
  }

  static sizeType(size: PostcardMailingSize): string {
    return match(size)
      .with('half', () => 'Half Sheet')
      .with('jumbo', () => 'Jumbo Sheet')
      .with('flats', () => '8.5" x 11" Flats')
      .exhaustive();
  }

  static paperType(paper: PostcardMailingPaper): string {
    return match(paper)
      .with('heavy_gloss', () => 'Heavy-gloss')
      .exhaustive();
  }

  static postageType(postage: PostcardMailingPostage): string {
    return match(postage)
      .with('standard', () => 'Bulk Mail')
      .with('first_class', () => 'First Class')
      .exhaustive();
  }

  static announcementType(announcement: PostcardAnnouncementType): string {
    return match(announcement)
      .with('just_listed', () => 'Just Listed')
      .with('sold', () => 'Sold')
      .with('other', () => 'Other')
      .exhaustive();
  }

  isDelivery(): boolean {
    return true;
  }

  isPrint(): boolean {
    return true;
  }

  defaultValue(): ZodVersionedMetadata<(typeof BrePostcardsMailingConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      count: 250,
      paper: 'heavy_gloss',
      size: 'half',
      postage: 'standard',
      announcement_type: 'other',
      need_by: datetime(this.order.created, this.order.timezone).toSQLDate(),
    };
  }

  revenueLines(): TenantJobLine[] {
    const { count, paper, size, postage, design } = this.metadata;
    const printRate = BrePostcardsMailingRate[size][paper].find(isUpTo(count));
    const handleRate = BrePostcardsHandleRate[size].find(isUpTo(count));
    const postageRate = BrePostcardsPostageRate[size][postage];
    const lines: TenantJobLine[] = [];

    if (printRate?.value) {
      lines.push({
        id: 'postcards_mailing',
        description: `${this.sizeType} ${this.paperType} post cards (x${count})`,
        amount: new Big(printRate.value),
        taxable: true,
      });
    }

    if (postageRate) {
      lines.push({
        id: 'postcards_mailing_postage',
        description: `${this.postageType} postage (x${count})`,
        amount: new Big(postageRate).times(count),
        taxable: true,
      });
    }

    if (handleRate?.value) {
      lines.push({
        id: 'postcards_mailing_handle',
        description: `Mailing service (x${count})`,
        amount: new Big(handleRate.value),
        taxable: true,
      });
    }

    if (design) {
      const rate = BreDesignRate[design][`postcard_${size}`];

      if (rate) {
        lines.push({
          id: 'porcard_mailing_design',
          description: `Postcard design (${design} layout)`,
          amount: new Big(rate),
        });
      }
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const { count, paper, size, postage, design } = this.metadata;
    const printRate = BrePostcardsMailingPay[size][paper].find(isUpTo(count));
    const handleRate = BrePostcardsHandlePay[size].find(isUpTo(count));
    const postageRate = BrePostcardsPostagePay[size][postage];
    const lines: TenantJobLine[] = [];

    if (printRate?.value) {
      lines.push({
        id: 'postcards_mailing',
        description: `${this.sizeType} ${this.paperType} post cards (x${count})`,
        amount: new Big(printRate.value),
      });
    }

    if (postageRate) {
      lines.push({
        id: 'postcards_mailing_postage',
        description: `${this.postageType} postage (x${count})`,
        amount: new Big(postageRate).times(count),
      });
    }

    if (handleRate?.value) {
      lines.push({
        id: 'postcards_mailing_handle',
        description: `Mailing service (x${count})`,
        amount: new Big(handleRate.value),
      });
    }

    if (design) {
      const pay = BreDesignPay[design][`postcard_${size}`];

      if (pay) {
        lines.push({
          id: 'porcard_mailing_design',
          description: `Postcard design (${design} layout)`,
          amount: new Big(pay),
        });
      }
    }

    return lines;
  }
}
